import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { AccountItemBlock } from "./AccountItem";
import HeaderImageUpload from "./HeaderImageUpload";
import HeaderTextBox from "./HeaderTextBox";
import { useUploadProgress } from "../../hooks";
import { submitAttachments } from "../attachments/attachment-utils";
import { alert } from "../common/Alert";
import { checkisAllowedAndDownload } from "../common/clinical/NotePDF";
import { DSButton } from "../design-system/molecules";

const UPLOAD_HEADER_IMAGE = gql`
  mutation uploadHeaderImage(
    $logoStorageObjectName: String!
    $logoContentType: String!
  ) {
    uploadHeaderImage(
      logoStorageObjectName: $logoStorageObjectName
      logoContentType: $logoContentType
    ) {
      ok
    }
  }
`;

const EDIT_PDF_SETTINGS = gql`
  mutation editPdfSettings($headerInfoPdf: String) {
    editPdfSettings(headerInfoPdf: $headerInfoPdf) {
      ok
    }
  }
`;

export default function PDFCustomizationSettings({
  data,
  refetch,
  loading,
}) {
  const [isEditing, setIsEditing] = useState(false);

  const { control, getValues, setValue, reset } = useForm({
    defaultValues: {
      headerInfoPdf: "",
    },
  });

  useEffect(() => {
    if (data?.user?.account?.headerInfoPdf !== undefined) {
      reset({ headerInfoPdf: data.user.account.headerInfoPdf ?? "" });
    }
  }, [data?.user?.account?.headerInfoPdf, reset]);

  const [uploadHeaderImage] = useMutation(UPLOAD_HEADER_IMAGE);
  const [editPdfSettings] = useMutation(EDIT_PDF_SETTINGS);

  const { setPercentageUploaded, setCurrentFileName } =
    useUploadProgress();

  const onSubmit = async () => {
    const values = getValues();
    let hasSuccess = false;

    if (values?.headerInfoPdf !== undefined) {
      try {
        await editPdfSettings({
          variables: {
            headerInfoPdf: values.headerInfoPdf,
          },
        });
        mixpanel.track("Updated Account Setting", {
          settingName: "PDF Additional Info",
          settingKey: "account.headerInfoPdf",
          settingValue: values.headerInfoPdf,
        });
        hasSuccess = true;
        alert("success", "Updated PDF header text.");
      } catch {
        alert(
          "error",
          "An error has occurred. Please try again later or contact support.",
        );
      }
    }

    if (values?.attachments) {
      const uploadedFiles = await submitAttachments({
        getValues,
        setPercentageUploaded,
        setCurrentFileName,
      });

      if (uploadedFiles) {
        try {
          await uploadHeaderImage({
            variables: {
              logoStorageObjectName:
                uploadedFiles[0].storageObjectName,
              logoContentType: uploadedFiles[0].contentType,
            },
          });
          mixpanel.track("Updated Account Setting", {
            settingName: "PDF Custom Image",
            settingKey: "account.logoStorageObjectName",
            settingValue: uploadedFiles[0].storageObjectName,
          });
          hasSuccess = true;
          alert("success", "Upload successful");
        } catch {
          alert(
            "error",
            "An error has occurred. Please try again later or contact support.",
          );
        }
      } else {
        alert(
          "error",
          "An error has occurred. Please try again later or contact support.",
        );
      }
    }

    if (hasSuccess) {
      try {
        await refetch();
      } catch {
        // Mutation succeeded but refetch failed — continue with cleanup
        // so the UI exits editing mode. Data will be stale until next navigation.
      }
    }
    setValue("attachments", null);
    setIsEditing(false);
  };

  function renderCancelSaveButtons() {
    return (
      <div className="flex flex-row items-center gap-sm">
        <DSButton
          variant="cancel"
          size="sm"
          onClick={() => {
            reset();
            setIsEditing(false);
          }}
          dataTestId="pdf-cancel-button"
        >
          Cancel
        </DSButton>
        <DSButton
          variant="primary"
          size="sm"
          onClick={() => onSubmit()}
          dataTestId="pdf-save-button"
        >
          Save and Upload
        </DSButton>
      </div>
    );
  }

  function renderEditPreviewButtons() {
    return (
      <div className="flex flex-row items-center gap-sm">
        <DSButton
          variant="secondary"
          size="sm"
          onClick={() =>
            checkisAllowedAndDownload(
              {
                isTagged: true,
                quickNote: "Your logo looks great here!",
                customObjectiveFields: null,
                shouldUseGenericObjective: true,
                subjective: "Subjective preview",
                objective: "Objective preview",
                assessment: "Assessment preview",
                plan: "Plan preview",
                createdAt: moment().format(),
              },
              {
                name: "Otto (example patient)",
                species: "dog",
                breed: "Golden Retriever",
                sex: "m",
                weight: "70 lbs",
                age: "~2 years old",
                ownerContactInfo: "Scribenote Inc.",
                emergencyContactInfo: "Scribenote's fans.",
              },
              data?.user,
            )
          }
          dataTestId="pdf-download-preview-button"
        >
          Download Preview
        </DSButton>
        <DSButton
          variant="primary"
          size="sm"
          onClick={() => setIsEditing(true)}
          dataTestId="pdf-edit-button"
        >
          Edit
        </DSButton>
      </div>
    );
  }

  return (
    <div
      id="headerImageUpload"
      className="flex-1 flex flex-col gap-md"
    >
      <div className="flex flex-col flex-1 w-full space-y-4 divide-y divide-secondary border-b md:border-0 border-secondary pb-4">
        <AccountItemBlock
          title="Additional Info:"
          desc="For including
            information such as clinic name, phone number, or email
            address at the top of your PDF exports."
        >
          <HeaderTextBox
            control={control}
            setIsEditing={setIsEditing}
          />
        </AccountItemBlock>

        <AccountItemBlock
          title="Custom Image:"
          desc="Upload a custom logo or
                image which will appear at the top of your PDF
                exports."
        >
          <HeaderImageUpload
            data={data}
            refetch={refetch}
            loading={loading}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            control={control}
          />
        </AccountItemBlock>
      </div>
      <div className="flex self-center md:justify-self-end md:self-end">
        {isEditing
          ? renderCancelSaveButtons()
          : renderEditPreviewButtons()}
      </div>
    </div>
  );
}
